<template>
  <div class="container mx-auto py-6 px-4 sm:px-6 lg:px-8">
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
      <div>
        <h1 class="text-2xl font-semibold text-gray-900">注单对比</h1>
        <p class="mt-1 text-sm text-gray-500">比较供应商与后台的注单数据</p>
      </div>
      <div class="mt-4 md:mt-0 flex space-x-3">
        <router-link 
          to="/admin/bets" 
          class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <i class="fas fa-list mr-2"></i>
          注单列表
        </router-link>
        <router-link 
          to="/admin/bets/reports" 
          class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <i class="fas fa-chart-bar mr-2"></i>
          注单报表
        </router-link>
      </div>
    </div>

    <!-- 筛选条件 -->
    <div class="mb-6">
      <SearchFilters
        :filter-config="filterConfig"
        :initial-filters="filters"
        @search="onSearch"
        @reset="resetFilters"
        @update:filters="updateFilters"
        ref="searchFiltersRef"
      >
        <template #after-buttons>
          <button
            v-if="!loading && comparisonData && comparisonData.list && comparisonData.list.length > 0"
            @click="exportBetsData"
            class="btn btn-success"
          >
            <i class="fas fa-download mr-1.5"></i> 导出数据
          </button>
        </template>
      </SearchFilters>
    </div>

    <!-- 加载中状态 -->
    <div v-if="loading" class="flex justify-center my-12">
      <div class="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm text-blue-600 transition ease-in-out duration-150">
        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        正在加载数据...
      </div>
    </div>

    <!-- 数据统计卡片 -->
    <div v-if="!loading && comparisonData" class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <!-- 总注单 -->
      <div class="bg-white overflow-hidden shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0 bg-blue-500 rounded-md p-3">
              <i class="fas fa-file-invoice text-white text-xl"></i>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">总处理注单</dt>
                <dd>
                  <div class="text-lg font-medium text-gray-900">{{ comparisonData.total_processed || 0 }}</div>
                </dd>
              </dl>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-4 py-4 sm:px-6">
          <div class="text-sm">
            <span class="font-medium text-blue-700">总获取: {{ comparisonData.total_fetched || 0 }}</span>
            <span class="mx-2">|</span>
            <span class="font-medium text-blue-700">总页数: {{ comparisonData.total_pages || 0 }}</span>
          </div>
        </div>
      </div>

      <!-- 成功注单 -->
      <div class="bg-white overflow-hidden shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0 bg-green-500 rounded-md p-3">
              <i class="fas fa-check text-white text-xl"></i>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">保存成功</dt>
                <dd>
                  <div class="text-lg font-medium text-gray-900">{{ comparisonData.saved || 0 }}</div>
                </dd>
              </dl>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-4 py-4 sm:px-6">
          <div class="text-sm">
            <span class="font-medium text-green-700">
              跳过: {{ comparisonData.skipped || 0 }}
            </span>
            <span class="mx-2">|</span>
            <span class="font-medium text-green-700">
              无差异: {{ comparisonData.different === 0 ? '是' : '否' }}
            </span>
          </div>
        </div>
      </div>

      <!-- 错误状态 -->
      <div class="bg-white overflow-hidden shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0 bg-red-500 rounded-md p-3">
              <i class="fas fa-exclamation-triangle text-white text-xl"></i>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">错误数量</dt>
                <dd>
                  <div class="text-lg font-medium text-gray-900">{{ comparisonData.error || 0 }}</div>
                </dd>
              </dl>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-4 py-4 sm:px-6">
          <div class="text-sm">
            <span class="font-medium text-red-700">
              未找到: {{ comparisonData.not_found || 0 }}
            </span>
            <span v-if="comparisonData.error_message" class="block mt-1 text-red-600">
              {{ comparisonData.error_message }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- 注单列表 -->
    <div v-if="!loading && comparisonData && comparisonData.list && comparisonData.list.length > 0" class="bg-white shadow rounded-lg mb-6">
      <div class="px-4 py-5 sm:px-6 flex justify-between items-center">
        <h3 class="text-lg leading-6 font-medium text-gray-900">注单列表</h3>
        <div class="text-sm text-gray-500">
          共 {{ comparisonData.total_processed || 0 }} 条记录
        </div>
      </div>
      
      <TableList
        :items="comparisonData.list || []"
        :columns="betComparisonColumns"
        :loading="loading"
        :has-pagination="comparisonData.total_pages > 1"
        :pagination="pagination"
        @change-page="handlePageChange"
      >
        <template #cell-fundsn="{ item }">
          <div class="text-sm text-blue-600 cursor-pointer hover:underline" @click="showDetailModal(item)">
            {{ item.fundsn }}
          </div>
        </template>
        
        <template #cell-vendor_username="{ item }">
          <div class="text-sm">{{ item.vendor_username }}</div>
        </template>
        
        <template #cell-game_info="{ item }">
          <div class="text-sm">
            <div>{{ item.game || '未知' }}</div>
            <div class="text-xs text-gray-500 mt-1">{{ item.game_category || '未分类' }}</div>
          </div>
        </template>
        
        <template #cell-bet_amount="{ item }">
          <div class="text-sm font-medium">{{ formatAmount(item.bet_amount, item.currency) }}</div>
        </template>
        
        <template #cell-payout="{ item }">
          <div class="text-sm font-medium" :class="item.payout > 0 ? 'text-green-600' : ''">
            {{ formatAmount(item.payout, item.currency) }}
          </div>
        </template>
        
        <template #cell-status="{ item }">
          <div class="text-sm">
            <span :class="getStatusClass(item.status)">
              {{ getStatusText(item.status) }}
            </span>
          </div>
        </template>
        
        <template #cell-bet_time="{ item }">
          <div class="text-sm">
            {{ formatUnixTime(item.bet_at) }}
          </div>
        </template>
        
        <template #cell-actions="{ item }">
          <button
            @click="showDetailModal(item)"
            class="text-blue-600 hover:text-blue-900 focus:outline-none"
          >
            <i class="fas fa-search mr-1"></i>
            查看
          </button>
        </template>
      </TableList>
    </div>

    <div v-else-if="!loading && comparisonData && (!comparisonData.list || comparisonData.list.length === 0)" class="bg-white shadow rounded-lg p-8 text-center text-gray-500">
      <i class="fas fa-info-circle text-blue-500 text-4xl mb-4"></i>
      <p>当前条件下没有查询到注单数据。</p>
      <p class="text-sm mt-2">请尝试调整筛选条件后重新查询。</p>
    </div>

    <div v-else-if="!loading && !comparisonData" class="bg-white shadow rounded-lg p-8 text-center text-gray-500">
      <i class="fas fa-search text-blue-500 text-4xl mb-4"></i>
      <p>请设置筛选条件并点击"搜索"按钮查看注单对比数据</p>
      <p class="text-sm mt-2">默认时间范围为过去30分钟的数据，支持精确到时分秒的选择</p>
    </div>

    <!-- 详情模态框 -->
    <BetComparisonDetailModal
      :visible="detailModalVisible"
      :comparison-data="selectedBet"
      @close="detailModalVisible = false"
      @fix="handleFixBet"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import TableList from '@/components/common/TableList.vue';
import DateRangePicker from '@/components/common/DateRangePicker.vue';
import BetComparisonDetailModal from '@/components/bets/BetComparisonDetailModal.vue';
import SearchFilters from '@/components/common/SearchFilters.vue';
import AdminApiClient from '@/utils/admin-api-client';
import { formatDate, formatUnixTime } from '@/utils/date-utils.js';
import { useToast } from '@/composables/useToast.js';

const { showToast } = useToast();
const apiClient = new AdminApiClient();
const vendorOptions = ref([]);
const currencyOptions = ref([]);
// 列表配置
const betComparisonColumns = [
  { key: 'fundsn', title: '注单ID' },
  { key: 'vendor_username', title: '用户名' },
  { key: 'game_info', title: '游戏信息' },
  { key: 'bet_amount', title: '投注金额' },
  { key: 'payout', title: '派彩金额' },
  { key: 'status', title: '状态' },
  { key: 'bet_time', title: '投注时间' },
  { key: 'actions', title: '操作' },
];
// 搜索过滤组件引用
const searchFiltersRef = ref(null);
// 初始筛选值
const filters = reactive({
  vendor: '',
  start_time: formatDate(new Date(Date.now() - 30 * 60 * 1000), 'yyyy-MM-dd HH:mm:ss'), // 30分钟前，包含时间
  end_time: formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss'), // 当前时间，包含时间
  category: '',
  currency: '',
  system: '', // 系统(集成商)筛选
  timezone: 'UTC+8' // 默认东八区
});

// 筛选配置
const filterConfig = ref([
  {
    id: 'vendor',
    type: 'select',
    label: '供应商',
    placeholder: '所有供应商',
    icon: 'fas fa-building',
    options: [
      { value: 'Panda', label: 'HH88体育' },
      { value: 'Cmd', label: 'Cmd体育' },
    ]
    // options: vendorOptions
  },
  {
    id: 'system',
    type: 'select',
    label: '集成商',
    placeholder: '不选择则是直对厂家',
    icon: 'fas fa-cogs',
    options: [
      { value: 'Wc', label: 'Wc' },
      // { value: 'One', label: 'One' },
      // { value: 'Imone', label: 'Imone' },
      // { value: 'Awc', label: 'Awc' },
      // { value: 'Dc', label: 'Dc' },
      // { value: 'Qtech', label: 'Qtech' },
      // { value: 'Bt', label: 'Bt' }
    ]
  },
  {
    id: 'dateRange',
    type: 'dateRange',
    label: '日期范围',
    icon: 'fas fa-calendar-alt',
    startField: 'start_time',
    endField: 'end_time',
    placeholder: '选择日期时间范围', 
    options: {
      enableTimePicker: true,
      format: 'yyyy-MM-dd HH:mm:ss'
    }
  },
  {
    id: 'timezone',
    type: 'select',
    label: '时区',
    placeholder: '选择时区',
    icon: 'fas fa-globe',
    options: [
      { value: 'UTC+0', label: 'UTC+0 (格林威治标准时间)' },
      { value: 'UTC+1', label: 'UTC+1 (中欧时间)' },
      { value: 'UTC+2', label: 'UTC+2 (东欧时间)' },
      { value: 'UTC+3', label: 'UTC+3 (莫斯科时间)' },
      { value: 'UTC+4', label: 'UTC+4 (杜拜时间)' },
      { value: 'UTC+5', label: 'UTC+5 (巴基斯坦时间)' },
      { value: 'UTC+6', label: 'UTC+6 (孟加拉时间)' },
      { value: 'UTC+7', label: 'UTC+7 (泰国时间)' },
      { value: 'UTC+8', label: 'UTC+8 (中国标准时间)' },
      { value: 'UTC+9', label: 'UTC+9 (日本标准时间)' },
      { value: 'UTC+10', label: 'UTC+10 (澳大利亚东部时间)' },
      { value: 'UTC+11', label: 'UTC+11 (所罗门群岛时间)' },
      { value: 'UTC+12', label: 'UTC+12 (新西兰时间)' },
      { value: 'UTC-1', label: 'UTC-1 (亚速尔群岛时间)' },
      { value: 'UTC-2', label: 'UTC-2 (南乔治亚岛时间)' },
      { value: 'UTC-3', label: 'UTC-3 (阿根廷时间)' },
      { value: 'UTC-4', label: 'UTC-4 (大西洋时间)' },
      { value: 'UTC-5', label: 'UTC-5 (东部时间)' },
      { value: 'UTC-6', label: 'UTC-6 (中部时间)' },
      { value: 'UTC-7', label: 'UTC-7 (山地时间)' },
      { value: 'UTC-8', label: 'UTC-8 (太平洋时间)' },
      { value: 'UTC-9', label: 'UTC-9 (阿拉斯加时间)' },
      { value: 'UTC-10', label: 'UTC-10 (夏威夷时间)' },
      { value: 'UTC-11', label: 'UTC-11 (萨摩亚群岛时间)' },
      { value: 'UTC-12', label: 'UTC-12 (国际日期变更线)' }
    ]
  },
  {
    id: 'category',
    type: 'select',
    label: '游戏类别',
    placeholder: '所有类别',
    icon: 'fas fa-gamepad',
    options: [
      { value: 'slot', label: '老虎机' },
      { value: 'casino', label: '真人娱乐场' },
      { value: 'fishing', label: '捕鱼' },
      { value: 'sport', label: '体育' },
      { value: 'lottery', label: '彩票' },
      { value: 'esport', label: '电子竞技' },
      { value: 'chess', label: '棋牌' },
      { value: 'arcade', label: '街机' },
      { value: 'qgame', label: '快速游戏' },
      { value: 'animal', label: '动物游戏' }
    ]
  },
  {
    id: 'currency',
    type: 'select',
    label: '币种',
    placeholder: '所有币种',
    icon: 'fas fa-money-bill-wave',
    options: currencyOptions
  }
]);

// 状态变量
const loading = ref(false);
const comparisonData = ref(null);
const detailModalVisible = ref(false);
const selectedBet = ref(null);
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0
});

// 初始化
onMounted(async () => {
  try {
    // 每次组件加载时，重设日期为当前时间
    resetDateToCurrentTime();
    await loadCompareOptions();
    // 默认不加载对比数据，等待用户点击搜索
  } catch (error) {
    console.error('初始化失败:', error);
    showToast('初始化数据失败，请刷新页面重试', 'error');
  }
});

// 监听日期变化
watch(
  () => [filters.start_time, filters.end_time],
  ([newStartTime, newEndTime], [oldStartTime, oldEndTime]) => {
    console.log('日期范围变化：', { 
      old: { start: oldStartTime, end: oldEndTime },
      new: { start: newStartTime, end: newEndTime } 
    });

    // 处理日期变化时的逻辑...
    if (searchFiltersRef.value) {
      const componentFilters = searchFiltersRef.value.filters;
      if (componentFilters) {
        // 检查组件内部日期是否与本地日期同步
        if (componentFilters.start_time !== newStartTime) {
          componentFilters.start_time = newStartTime;
        }
        if (componentFilters.end_time !== newEndTime) {
          componentFilters.end_time = newEndTime;
        }
      }
    }
  }
);

// 重置日期为当前时间
function resetDateToCurrentTime() {
  const now = new Date();
  const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);
  
  // 格式化日期时间，确保包含秒
  filters.start_time = formatDate(thirtyMinutesAgo, 'yyyy-MM-dd HH:mm:ss');
  filters.end_time = formatDate(now, 'yyyy-MM-dd HH:mm:ss');
  
  console.log('日期已重置为当前时间:', filters.start_time, '至', filters.end_time);
}

// 根据时区调整日期
function adjustDateForTimezone(date, timezone) {
  if (!date || !timezone) return date;
  
  const originalDate = new Date(date);
  if (isNaN(originalDate.getTime())) return date;
  
  // 获取时区偏移量（小时）
  let targetOffset = 0;
  try {
    const match = timezone.match(/UTC([+-]\d+)/);
    if (match && match[1]) {
      targetOffset = parseInt(match[1]);
    }
  } catch (e) {
    console.error('解析时区偏移量失败:', e);
    return date;
  }
  
  // 获取当前系统时区偏移（分钟），转换为小时
  const systemOffset = -originalDate.getTimezoneOffset() / 60;
  
  // 计算时区差异（小时）
  const hoursDiff = targetOffset - systemOffset;
  
  // 调整日期，小时差 * 60分钟 * 60秒 * 1000毫秒
  const adjustedDate = new Date(originalDate.getTime() + (hoursDiff * 60 * 60 * 1000));
  console.log(`时区调整: ${date} 从 UTC${systemOffset >= 0 ? '+' : ''}${systemOffset} 到 ${timezone} = ${formatDate(adjustedDate, 'yyyy-MM-dd HH:mm:ss')}`);
  
  return formatDate(adjustedDate, 'yyyy-MM-dd HH:mm:ss');
}

// 更新筛选条件
function updateFilters(newFilters) {
  console.log('筛选条件更新:', newFilters);
  
  // 更新本地filters对象
  Object.keys(newFilters).forEach(key => {
    filters[key] = newFilters[key];
  });
  
  // 特别处理日期选择器的值，确保同步
  if (newFilters.start_time || newFilters.end_time) {
    console.log('日期范围更新:', newFilters.start_time, newFilters.end_time);
  }
  
  // 在console中显示过滤条件的当前值
  console.log('当前过滤条件值:', filters);
}

// 处理搜索按钮点击
function onSearch() {
  console.log('执行搜索，筛选条件:', filters);
  
  // 确保SearchFilters组件中的值已同步到本地filters
  if (searchFiltersRef.value) {
    // 获取组件内部的filters值
    const componentFilters = searchFiltersRef.value.filters;
    if (componentFilters) {
      console.log('SearchFilters组件内部filters:', componentFilters);
      // 同步到本地filters
      Object.keys(componentFilters).forEach(key => {
        filters[key] = componentFilters[key];
      });
    }
  }
  
  loadComparisonData();
}

// 加载比较选项（供应商和币种列表）
async function loadCompareOptions() {
  try {
    const response = await apiClient.getCompareOptions();
    console.log('比较选项数据:', response);
    
    // 更新供应商选项
    if (response.data?.vendors) {
      // 处理对象格式的vendors数据
      if (typeof response.data.vendors === 'object' && !Array.isArray(response.data.vendors)) {
        const options = [];
        Object.entries(response.data.vendors).forEach(([key, value]) => {
          // 跳过默认选项
          if (key !== "") {
            options.push({ value: key, label: value });
          }
        });
        vendorOptions.value = options;
      } 
      // 处理数组格式的vendors数据（兼容）
      else if (Array.isArray(response.data.vendors)) {
        vendorOptions.value = response.data.vendors.map(vendor => ({
          value: vendor.key,
          label: vendor.name
        }));
      }
    }
    
    // 更新币种选项
    if (response.data?.currencies) {
      // 处理对象格式的currencies数据
      if (typeof response.data.currencies === 'object' && !Array.isArray(response.data.currencies)) {
        const options = [];
        Object.entries(response.data.currencies).forEach(([key, value]) => {
          // 跳过默认选项
          if (key !== "") {
            options.push({ value: key, label: `${key} - ${value}` });
          }
        });
        currencyOptions.value = options;
      } 
      // 处理数组格式的currencies数据（兼容）
      else if (Array.isArray(response.data.currencies)) {
        currencyOptions.value = response.data.currencies.map(currency => ({
          value: currency.code,
          label: `${currency.code} - ${currency.name}`
        }));
      }
    }
    
    // 不自动加载对比数据，用户需要手动点击搜索
    // await loadComparisonData();
  } catch (error) {
    console.error('加载比较选项失败:', error);
    showToast('加载供应商和币种选项失败', 'error');
  }
}

// 加载对比数据
async function loadComparisonData() {
  try {
    loading.value = true;
    console.log('Loading comparison data with filters:', filters);
    
    // 检查日期是否为未来日期
    const now = new Date();
    const startTime = new Date(filters.start_time);
    const endTime = new Date(filters.end_time);
    
    if (startTime > now) {
      filters.start_time = formatDate(new Date(Date.now() - 30 * 60 * 1000), 'yyyy-MM-dd HH:mm:ss');
    }
    if (endTime > now) {
      filters.end_time = formatDate(now, 'yyyy-MM-dd HH:mm:ss');
    }
    
    // 验证游戏类别是否已选择
    if (!filters.category) {
      showToast('请选择游戏类别', 'error');
      loading.value = false;
      return;
    }
    
    // 根据时区调整日期
    let adjustedStartTime = filters.start_time;
    let adjustedEndTime = filters.end_time;
    
    if (filters.timezone) {
      adjustedStartTime = adjustDateForTimezone(filters.start_time, filters.timezone);
      adjustedEndTime = adjustDateForTimezone(filters.end_time, filters.timezone);
      console.log('根据时区调整后的日期:', adjustedStartTime, '至', adjustedEndTime);
    }
    
    // 构建请求参数，确保与API期望的参数名一致
    const params = {
      page: pagination.currentPage,
      per_page: pagination.pageSize,
      vendor: filters.vendor || '',
      system: filters.system || '',
      start_time: adjustedStartTime,
      end_time: adjustedEndTime,
      category: filters.category || '',
      currency: filters.currency || '',
      timezone: filters.timezone || 'UTC+8' // 添加时区参数
    };
    
    // 在控制台记录请求参数
    console.log('Request params:', params);
    
    // 调用API
    const response = await apiClient.getBetComparison(params);
    
    if (response.success) {
      comparisonData.value = response.data;
      pagination.total = response.data.total_processed || 0;
      pagination.currentPage = response.data.page || 1;
      pagination.pageSize = response.data.per_page || 10;
      
      console.log('Comparison data loaded:', comparisonData.value);
    } else {
      showToast(response.message || '加载数据失败', 'error');
    }
  } catch (error) {
    console.error('Failed to load comparison data:', error);
    showToast('加载对比数据失败: ' + (error.message || '未知错误'), 'error');
  } finally {
    loading.value = false;
  }
}

// 格式化金额
function formatAmount(amount, currency) {
  if (amount === null || amount === undefined) return '-';
  
  const numAmount = parseFloat(amount);
  const formatter = new Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${formatter.format(numAmount)} ${currency || ''}`.trim();
}

// 计算百分比
function calculatePercentage(part, total) {
  if (!total) return '0.00';
  const percentage = (part / total) * 100;
  return percentage.toFixed(2);
}

// 获取状态样式类
function getStatusClass(status) {
  switch (status) {
    case 'win':
      return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800';
    case 'lose':
      return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800';
    case 'draw':
      return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800';
    case 'cancel':
      return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
    case 'pending':
      return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800';
    default:
      return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
  }
}

// 处理页码变化
function handlePageChange(page) {
  pagination.currentPage = page;
  loadComparisonData();
}

// 重置筛选条件
function resetFilters() {
  console.log('重置筛选条件');
  
  // 使用SearchFilters组件的resetFilters方法
  if (searchFiltersRef.value) {
    searchFiltersRef.value.resetFilters();
    console.log('SearchFilters组件已重置');
  }
  
  // 同时重置本地filters对象
  filters.vendor = '';
  // 重置为当前时间
  resetDateToCurrentTime();
  filters.category = '';
  filters.currency = '';
  filters.system = ''; // 重置系统筛选
  filters.timezone = 'UTC+8'; // 重置为默认时区
  
  pagination.currentPage = 1;
  
  // 提示用户需要点击搜索按钮
  showToast('筛选条件已重置，请点击搜索按钮查询数据', 'info');
}

// 显示详情模态框
function showDetailModal(bet) {
  selectedBet.value = bet;
  detailModalVisible.value = true;
}

// 处理修复注单
async function handleFixBet(fixData) {
  try {
    loading.value = true;
    
    // 调用修复API
    await apiClient.fixBetMismatch({
      fundsn: fixData.fundsn,
      fix_type: fixData.fix_type
    });
    
    showToast('注单修复成功!', 'success');
    
    // 重新加载数据
    await loadComparisonData();
  } catch (error) {
    console.error('修复注单失败:', error);
    showToast('修复注单失败: ' + (error.message || '未知错误'), 'error');
  } finally {
    loading.value = false;
  }
}

// 导出注单数据
function exportBetsData() {
  try {
    if (!comparisonData.value || !comparisonData.value.list || comparisonData.value.list.length === 0) {
      showToast('没有可导出的数据', 'warning');
      return;
    }
    
    // 构建CSV内容
    const headers = ['注单ID', '用户名', '游戏', '游戏类别', '投注金额', '派彩金额', '币种', '状态', '投注时间'];
    let csvContent = headers.join(',') + '\r\n';
    
    comparisonData.value.list.forEach(bet => {
      const row = [
        bet.fundsn,
        bet.vendor_username,
        bet.game,
        bet.game_category,
        bet.bet_amount,
        bet.payout,
        bet.currency,
        getStatusText(bet.status),
        formatUnixTime(bet.bet_at)
      ];
      
      csvContent += row.join(',') + '\r\n';
    });
    
    // 创建下载链接
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `注单报告_${formatDate(new Date())}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showToast('导出成功', 'success');
  } catch (error) {
    console.error('导出数据失败:', error);
    showToast('导出数据失败', 'error');
  }
}

// 获取状态文本
function getStatusText(status) {
  switch (parseInt(status)) {
    case 0: return '未结算';
    case 1: return '已结算';
    case 2: return '已取消';
    default: return '未知状态';
  }
}
</script>

<style scoped>
.btn {
  @apply inline-flex items-center justify-center px-4 py-2.5 border text-sm font-medium rounded-md
  shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 
  transition-all duration-200 ease-in-out;
}

.btn-success {
  @apply border-transparent text-white bg-gradient-to-r from-green-500 to-green-600
  hover:from-green-600 hover:to-green-700 hover:shadow-md
  focus:ring-green-500;
}
</style> 